import { formatTimeCompact } from "@/lib/timeUtils";
import { defineStore } from "pinia";
import { ref, watch } from "vue";
import { getStores, getShifts } from "../lib/api";

export const useCalendarStore = defineStore("calendar", () => {
  // State
  const events = ref([]);
  const stores = ref([]);
  const selectedStore = ref(null);
  const isLoading = ref(false);
  const error = ref(null);

  // Try to restore selectedStore from localStorage
  try {
    const storedStoreId = localStorage.getItem("selected_store_id");
    if (storedStoreId) {
      selectedStore.value = { id: parseInt(storedStoreId, 10) };
    }
  } catch (e) {
    console.error("Error reading from localStorage:", e);
  }

  // Persist to localStorage on change
  watch(
    () => selectedStore.value,
    (val) => {
      if (val?.id) {
        try {
          localStorage.setItem("selected_store_id", val.id);
        } catch (e) {
          console.error("Error writing to localStorage:", e);
        }
      }
    },
    { deep: true }
  );

  // Format time for calendar display (compact: 8AM-4PM or 8:45-3PM)
  const formatTimeLabel = (start, end, name) => {
    return `${formatTimeCompact(start)}-${formatTimeCompact(end)} ${name}`;
  };

  // Fetch all stores
  const fetchStores = async () => {
    isLoading.value = true;
    try {
      const response = await getStores();
      stores.value = response.data;

      // Match real store object to stored ID
      if (selectedStore.value && selectedStore.value.id) {
        const match = stores.value.find((s) => s.id === selectedStore.value.id);
        if (match) {
          selectedStore.value = match;
        } else {
          selectedStore.value = stores.value[0] || null;
        }
      } else if (!selectedStore.value && stores.value.length > 0) {
        selectedStore.value = stores.value[0];
      }

      return response.data;
    } catch (err) {
      error.value = err.message || "Failed to fetch stores";
      return [];
    } finally {
      isLoading.value = false;
    }
  };

  // Fetch shifts for the current view
  const fetchShifts = async (start, end, staffMap) => {
    isLoading.value = true;
    try {
      const params = { start_date: start, end_date: end };
      if (selectedStore.value?.id) params.store_id = selectedStore.value.id;

      const response = await getShifts(params);

      events.value = response.data.map((shift) => {
        const userId = shift.user_id;
        const staffMember = staffMap.get(userId);
        const userName = staffMember ? staffMember.name : "Staff";
        const userColor = staffMember ? staffMember.color_code : "#3498db";

        // Parse the UTC ISO strings to get formatted times for the title
        const formattedTitle = formatTimeLabel(shift.start_time, shift.end_time, userName);

        return {
          id: shift.id,
          title: formattedTitle,
          start: shift.start_time, // FullCalendar with timeZone will parse this correctly
          end: shift.end_time,
          userId: shift.user_id,
          backgroundColor: userColor,
          borderColor: userColor,
          extendedProps: {
            storeId: shift.store_id,
            notes: shift.notes,
            userId: shift.user_id,
          },
        };
      });

      return events.value;
    } catch (err) {
      error.value = err.message || "Failed to fetch shifts";
      return [];
    } finally {
      isLoading.value = false;
    }
  };

  return {
    // State
    events,
    stores,
    selectedStore,
    isLoading,
    error,
    // Actions
    fetchStores,
    fetchShifts,
  };
});
